import { defineStore } from 'pinia';
import { ref } from 'vue';

export type DialogType = 'alert' | 'confirm';

export interface DialogConfig {
    title: string;
    message: string;
    type: DialogType;
    isDanger?: boolean;
    confirmText?: string;
    cancelText?: string;
}

export const useUiStore = defineStore( 'ui', () => {
    // State
    const loading = ref<boolean>( false );
    const loadingMessage = ref<string>( 'Loading...' );
    const loadingSubtext = ref<string>( 'Please wait...' );
    // When a navigation-triggered loading is in progress we may want to
    // keep the spinner visible until the destination route has been
    // resolved/mounted. `navigationTarget` stores an optional route name
    // which, when set, will be used by the router hook to decide when to
    // clear the loading state.
    const navigationTarget = ref<string | null>( null );

    // When true, the home page should not auto-show the initial loading
    // overlay once after a navigation. This is used to prevent the home
    // page from showing its own loader immediately after a transition
    // initiated by a user clicking an assessment card.
    const suppressNextHomeLoading = ref<boolean>( false );
    // When true, the global quit-confirmation should be skipped. This is used
    // during controlled flows like Submit -> Results navigation so the user is
    // not prompted when following the proper submit flow.
    const suppressQuitConfirmation = ref<boolean>( false );

    const dialogVisible = ref<boolean>( false );
    const dialogConfig = ref<DialogConfig>( {
        title: '',
        message: '',
        type: 'alert'
    } );
    const dialogResolve = ref<( ( value: boolean ) => void ) | null>( null );

    // Actions
    function showLoading ( message: string = 'Loading...', subtext: string = 'Please wait...' ) {
        loading.value = true;
        loadingMessage.value = message;
        loadingSubtext.value = subtext;
    }

    /**
     * Show the global loading overlay and optionally mark a navigation
     * target. The router hook will clear the loading state when the
     * navigation completes or if the navigation target is reached.
     */
    function showLoadingForNavigation ( targetRouteName?: string, message: string = 'Loading...', subtext: string = 'Please wait...' ) {
        navigationTarget.value = targetRouteName || null;
        showLoading( message, subtext );
    }

    function hideLoading () {
        loading.value = false;
        // Clear any navigation expectation when the overlay is hidden
        // to avoid stale targets affecting subsequent navigations.
        navigationTarget.value = null;
    }

    function showAlert ( title: string, message: string ): Promise<void> {
        return new Promise( ( resolve ) => {
            dialogConfig.value = {
                title,
                message,
                type: 'alert',
                confirmText: 'OK'
            };
            dialogVisible.value = true;
            dialogResolve.value = () => {
                resolve();
            };
        } );
    }

    function showConfirm (
        title: string,
        message: string,
        isDanger: boolean = false
    ): Promise<boolean> {
        return new Promise( ( resolve ) => {
            dialogConfig.value = {
                title,
                message,
                type: 'confirm',
                isDanger,
                confirmText: 'Confirm',
                cancelText: 'Cancel'
            };
            dialogVisible.value = true;
            dialogResolve.value = resolve;
        } );
    }

    function closeDialog ( confirmed: boolean = false ) {
        dialogVisible.value = false;
        if ( dialogResolve.value ) {
            dialogResolve.value( confirmed );
            dialogResolve.value = null;
        }
    }

    function setSuppressNextHomeLoading ( v: boolean ) {
        suppressNextHomeLoading.value = !!v;
    }

    function setSuppressQuitConfirmation ( v: boolean ) {
        suppressQuitConfirmation.value = !!v;
    }

    return {
        // State
        loading,
        loadingMessage,
        loadingSubtext,
        navigationTarget,
        suppressNextHomeLoading,
        suppressQuitConfirmation,
        dialogVisible,
        dialogConfig,

        // Actions - loading/navigation
        showLoading,
        showLoadingForNavigation,
        hideLoading,
        setSuppressNextHomeLoading,
        setSuppressQuitConfirmation,

        // Actions - dialogs
        showAlert,
        showConfirm,
        closeDialog
    };
} );
